import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, IconButton, Button, ButtonGroup, FormControlLabel, Switch, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import RotateRightIcon from '@mui/icons-material/RotateRight';

interface BoundingBox {
  left: number;    // 0-1 normalized
  top: number;     // 0-1 normalized
  width: number;   // 0-1 normalized
  height: number;  // 0-1 normalized
}

interface FieldWithCoordinates {
  value: any;
  confidence: number;
  bounding_box?: BoundingBox;
  page?: number;
}

interface DocumentWithHighlightsProps {
  imageUrl: string;
  extractedData: Record<string, FieldWithCoordinates>;
  focusedField: string | null;
  onFieldClick?: (fieldName: string) => void;
  currentPage?: number;
}

const DocumentWithHighlights: React.FC<DocumentWithHighlightsProps> = ({
  imageUrl,
  extractedData,
  focusedField,
  onFieldClick,
  currentPage = 1
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [zoomLevel, setZoomLevel] = useState(0.5);
  const [rotation, setRotation] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  // When on, hovering a field on the data panel pans the viewer to center it.
  const [autoFollow, setAutoFollow] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Update dimensions when image loads
    const img = new Image();
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // When the focused field changes, pan the viewport so that field's highlight
  // is centered. Lets the user hover field-to-field on the data panel and have
  // the document jump to each field's exact location. Uses measured on-screen
  // rects so it works regardless of zoom / rotation / the padding wrapper.
  useEffect(() => {
    if (!focusedField || !autoFollow) return;
    const centerOnField = () => {
      const container = document.getElementById('doc-highlight-container');
      const highlight = document.getElementById(`highlight-${focusedField}`);
      if (!container || !highlight) return;
      const cRect = container.getBoundingClientRect();
      const hRect = highlight.getBoundingClientRect();
      const targetLeft =
        (hRect.left + hRect.width / 2) - cRect.left + container.scrollLeft - container.clientWidth / 2;
      const targetTop =
        (hRect.top + hRect.height / 2) - cRect.top + container.scrollTop - container.clientHeight / 2;
      container.scrollTo({ left: targetLeft, top: targetTop, behavior: 'smooth' });
    };
    // Wait for the newly-focused field's highlight div to be committed + painted.
    const raf = requestAnimationFrame(() => requestAnimationFrame(centerOnField));
    return () => cancelAnimationFrame(raf);
  }, [focusedField, autoFollow]);

  const getHighlightStyle = (bbox: BoundingBox, isActive: boolean, fieldName: string) => {
    if (!containerRef.current) return {};

    const containerRect = containerRef.current.getBoundingClientRect();

    const style = {
      position: 'absolute' as const,
      left: `${bbox.left * 100}%`,
      top: `${bbox.top * 100}%`,
      width: `${bbox.width * 100}%`,
      height: `${bbox.height * 100}%`,
      border: '3px solid #FFD700',
      backgroundColor: 'rgba(255, 215, 0, 0.3)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      zIndex: 10,
      boxShadow: '0 0 20px rgba(255, 215, 0, 0.6)'
    };

    if (isActive) {
      console.log(`HIGHLIGHTING ${fieldName} - Drawing box at:`, {
        left: `${bbox.left * 100}%`,
        top: `${bbox.top * 100}%`,
        width: `${bbox.width * 100}%`,
        height: `${bbox.height * 100}%`,
        rawBbox: bbox
      });
    }

    return style;
  };

  // Helper to extract bounding box from DynamoDB format
  const getBoundingBox = (fieldData: any) => {
    if (!fieldData) return null;

    // Handle DynamoDB M wrapper
    let data = fieldData;
    if (data.M) data = data.M;

    // Look for bounding_box
    let bbox = data.bounding_box;
    if (!bbox) return null;

    // Unwrap M wrapper if present
    if (bbox.M) bbox = bbox.M;

    // Extract coordinates from DynamoDB format
    const result: any = {};
    if (bbox.top) result.top = bbox.top.N ? parseFloat(bbox.top.N) : bbox.top;
    if (bbox.left) result.left = bbox.left.N ? parseFloat(bbox.left.N) : bbox.left;
    if (bbox.width) result.width = bbox.width.N ? parseFloat(bbox.width.N) : bbox.width;
    if (bbox.height) result.height = bbox.height.N ? parseFloat(bbox.height.N) : bbox.height;

    return (result.top !== undefined && result.left !== undefined) ? result : null;
  };

  // Get all fields that have bounding boxes for current page
  const fieldsOnPage: Array<[string, any]> = [];

  // Process regular fields
  Object.entries(extractedData).forEach(([fieldName, fieldData]) => {
    const bbox = getBoundingBox(fieldData);
    if (fieldName === 'meter_id' || fieldName === 'test_date' || fieldName === 'meter_factor' || fieldName === 'test_runs') {
      console.log(`Field ${fieldName}:`, fieldData);
      console.log(`Extracted bbox for ${fieldName}:`, bbox);
    }

    if (bbox && (!fieldData.page || fieldData.page === currentPage)) {
      fieldsOnPage.push([fieldName, fieldData]);
    }

    // Alias: the API Gravity cell displays the `density` value when api_gravity is
    // empty (SGS forms). Register density's box under 'api_gravity' too so hovering
    // that cell still locates it on the document.
    if (fieldName === 'density' && bbox) {
      const apiGravity = (extractedData as any)?.api_gravity;
      if (!getBoundingBox(apiGravity)) {
        fieldsOnPage.push(['api_gravity', fieldData]);
      }
    }

    // SGS flow_rate_averages: register each flow sub-field under the SAME dotted
    // key the data panel uses (flow_rate_averages.<i>.<field>) so Auto-Find matches
    // when the user hovers a flow card field.
    if (fieldName === 'flow_rate_averages') {
      const flows = (fieldData as any)?.value || (fieldData as any)?.L || fieldData;
      if (Array.isArray(flows)) {
        flows.forEach((flow: any, idx: number) => {
          const flowData = flow.M || flow;
          Object.entries(flowData || {}).forEach(([subName, subData]: [string, any]) => {
            const subBbox = getBoundingBox(subData);
            if (subBbox) {
              fieldsOnPage.push([`flow_rate_averages.${idx}.${subName}`, subData]);
            }
          });
        });
      }
    }

    // SGS nested objects (meter_under_test / reference_meter): register each
    // sub-field under the dotted key the data panel uses (e.g.
    // meter_under_test.serial_number) so Auto-Find works when the user hovers them.
    if (fieldName === 'meter_under_test' || fieldName === 'reference_meter') {
      const obj = (fieldData as any)?.value || (fieldData as any)?.M || fieldData;
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        Object.entries(obj).forEach(([subName, subData]: [string, any]) => {
          const subBbox = getBoundingBox(subData);
          if (subBbox) {
            fieldsOnPage.push([`${fieldName}.${subName}`, subData]);
          }
        });
      }
    }

    // Special handling for test_runs array
    if (fieldName === 'test_runs') {
      const runs = (fieldData as any)?.value || (fieldData as any)?.L || fieldData;
      if (Array.isArray(runs)) {
        runs.forEach((run: any, runIndex: number) => {
          // Each run may have multiple fields with bounding boxes
          const runData = run.M || run;
          Object.entries(runData || {}).forEach(([runFieldName, runFieldData]: [string, any]) => {
            const runBbox = getBoundingBox(runFieldData);
            if (runBbox) {
              const compositeFieldName = `test_run_${runIndex + 1}_${runFieldName}`;
              console.log(`Test run field ${compositeFieldName}:`, runFieldData, 'bbox:', runBbox);
              fieldsOnPage.push([compositeFieldName, runFieldData]);
            }
          });
        });
      }
    }

    // Also check nested structures like averages, corrections, final_results
    ['averages', 'corrections', 'final_results', 'additional_fields'].forEach(nestedField => {
      if (fieldName === nestedField && typeof fieldData === 'object') {
        const nestedData = (fieldData as any)?.value || (fieldData as any)?.M || fieldData;
        if (nestedData && typeof nestedData === 'object') {
          Object.entries(nestedData).forEach(([subFieldName, subFieldData]: [string, any]) => {
            const subBbox = getBoundingBox(subFieldData);
            if (subBbox) {
              const compositeFieldName = `${nestedField}_${subFieldName}`;
              console.log(`Nested field ${compositeFieldName}:`, subFieldData, 'bbox:', subBbox);
              fieldsOnPage.push([compositeFieldName, subFieldData]);
            }
          });
        }
      }
    });
  });

  // Crude tickets have a nested schema (net_outturn.m3_at_15c, key_quality.density_dry,
  // additional_fields.*) that the proving-specific handlers above don't cover. For tickets
  // only (so proving behaviour is untouched), walk the tree generically and register every
  // leaf that has a bounding_box under the SAME dotted key the data panel focuses on.
  const isTicketData = (extractedData as any)?.document_type === 'crude_ticket_kdr' ||
                       (extractedData as any)?.document_type === 'crude_ticket';
  if (isTicketData) {
    const metaKeys = new Set(['value', 'confidence', 'source_text', 'source_page', 'bounding_box', 'page', 'less_than']);
    const collectNested = (node: any, prefix: string) => {
      if (!node || typeof node !== 'object') return;
      const box = getBoundingBox(node);
      if (box && prefix && !fieldsOnPage.some(([n]) => n === prefix)) {
        if (!node.page || node.page === currentPage) {
          fieldsOnPage.push([prefix, node]);
        }
      }
      Object.entries(node).forEach(([k, v]) => {
        if (metaKeys.has(k)) return;
        if (v && typeof v === 'object') {
          collectNested(v, prefix ? `${prefix}.${k}` : k);
        }
      });
    };
    Object.entries(extractedData).forEach(([k, v]) => collectNested(v, k));
  }

  console.log('Total fields with bounding boxes:', fieldsOnPage.length);
  console.log('Fields on page:', fieldsOnPage.map(([name]) => name));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Zoom Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
        <ButtonGroup size="small" variant="outlined">
          <IconButton
            onClick={() => setZoomLevel(Math.max(0.05, zoomLevel - 0.25))}
            disabled={zoomLevel <= 0.05}
            size="small"
            title="Zoom Out"
          >
            <ZoomOutIcon />
          </IconButton>
          <Button
            onClick={() => setZoomLevel(1)}
            size="small"
            sx={{ minWidth: '80px' }}
          >
            {Math.round(zoomLevel * 100)}%
          </Button>
          <IconButton
            onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
            disabled={zoomLevel >= 3}
            size="small"
            title="Zoom In"
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              // Reset to 50% zoom and center
              setZoomLevel(0.5);

              // Center the viewport after zoom change
              setTimeout(() => {
                const container = document.getElementById('doc-highlight-container');
                if (container) {
                  const centerX = (container.scrollWidth - container.clientWidth) / 2;
                  const centerY = (container.scrollHeight - container.clientHeight) / 2;
                  container.scrollLeft = centerX;
                  container.scrollTop = centerY;
                }
              }, 50);
            }}
            size="small"
            title="Reset to 50%"
          >
            <FitScreenIcon />
          </IconButton>
        </ButtonGroup>

        {/* Rotation Controls */}
        <ButtonGroup size="small" variant="outlined">
          <IconButton
            onClick={() => setRotation(rotation - 90)}
            size="small"
            title="Rotate Left"
            sx={{ transform: 'scaleX(-1)' }}
          >
            <RotateRightIcon />
          </IconButton>
          <IconButton
            onClick={() => setRotation(rotation + 90)}
            size="small"
            title="Rotate Right"
          >
            <RotateRightIcon />
          </IconButton>
        </ButtonGroup>

        {/* Auto-follow toggle: pan the viewer to the field you hover on the data panel */}
        <Tooltip title="When on, hovering a field on the right pans the document to that field's location">
          <FormControlLabel
            sx={{ ml: 0.5 }}
            control={
              <Switch
                size="small"
                checked={autoFollow}
                onChange={(e) => setAutoFollow(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Auto-Find</Typography>}
          />
        </Tooltip>
      </Box>

      {/* Document Image Container */}
      <Box
        id="doc-highlight-container"
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'rgba(0,0,0,0.02)',
          borderRadius: 1,
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onWheel={(e) => {
          // Zoom on Ctrl+Scroll or Cmd+Scroll
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.05), 3));
          }
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          setDragStart({ x: e.clientX, y: e.clientY });
          const container = document.getElementById('doc-highlight-container');
          if (container) {
            setScrollStart({ x: container.scrollLeft, y: container.scrollTop });
          }
        }}
        onMouseMove={(e) => {
          if (isDragging) {
            e.preventDefault();
            const container = document.getElementById('doc-highlight-container');
            if (container) {
              const deltaX = dragStart.x - e.clientX;
              const deltaY = dragStart.y - e.clientY;
              container.scrollLeft = scrollStart.x + deltaX;
              container.scrollTop = scrollStart.y + deltaY;
            }
          }
        }}
        onMouseUp={() => {
          setIsDragging(false);
        }}
        onMouseLeave={() => {
          setIsDragging(false);
        }}
      >
        <div style={{
          padding: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <div ref={containerRef} style={{
            position: 'relative',
            transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
            transformOrigin: 'center',
            transition: 'transform 0.1s',
          }}>
          <img
            src={imageUrl}
            alt="Document"
            style={{
              display: 'block',
              width: 'auto',
              height: 'auto',
            }}
            onLoad={() => {
              // Center the viewport after image loads
              if (!isInitialized) {
                const container = document.getElementById('doc-highlight-container');
                if (container) {
                  // Calculate center position
                  const centerX = (container.scrollWidth - container.clientWidth) / 2;
                  const centerY = (container.scrollHeight - container.clientHeight) / 2;
                  container.scrollLeft = centerX;
                  container.scrollTop = centerY;
                  setIsInitialized(true);
                }
              }
            }}
          />

          {/* Highlight Overlays - Only show for focused field */}
          {fieldsOnPage.map(([fieldName, fieldData]) => {
          const bbox = getBoundingBox(fieldData);
          if (!bbox) return null;

          const isActive = focusedField === fieldName;

          // Only show bounding box if this field is focused
          if (!isActive) return null;

          // Extract value and confidence from DynamoDB format
          const data = fieldData as any;
          let value = data;
          let confidence = 0;

          // Handle DynamoDB M wrapper structure
          if (data.M) {
            // Extract value
            if (data.M.value) {
              value = data.M.value.S || data.M.value;
            }
            // Extract confidence
            if (data.M.confidence && data.M.confidence.N) {
              confidence = parseFloat(data.M.confidence.N);
            }
          } else if (data.S) {
            // Simple string value
            value = data.S;
          } else if (data.value) {
            // Direct value property
            value = data.value;
            if (data.confidence) {
              confidence = typeof data.confidence === 'object' && data.confidence.N
                ? parseFloat(data.confidence.N)
                : data.confidence;
            }
          }

          console.log(`Field ${fieldName} - Value: ${value}, Confidence: ${confidence}`);

          return (
            <div
              key={fieldName}
              id={`highlight-${fieldName}`}
              style={getHighlightStyle(bbox, isActive, fieldName)}
              onClick={() => onFieldClick?.(fieldName)}
              title={`${fieldName}: ${value} (${(confidence * 100).toFixed(0)}%)`}
            >
              {isActive && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-25px',
                    left: '0',
                    backgroundColor: '#FFD700',
                    color: '#000',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {fieldName}
                </div>
              )}
            </div>
          );
        })}
          </div>
        </div>
      </Box>

      {/* Legend */}
      <Paper sx={{ mt: 1, p: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Field Highlighting
        </Typography>
        <Typography variant="caption" color="text.secondary">
          • Click on a field in the data panel to highlight its location on the document
          <br />
          • Gold highlight shows the currently selected field
          <br />
          {fieldsOnPage.length > 0
            ? `• ${fieldsOnPage.length} fields with location data available`
            : '• No coordinate data available for this document'}
        </Typography>
      </Paper>
    </Box>
  );
};

export default DocumentWithHighlights;